import "./new-employee-salary.scss";

import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, FormProvider, useForm } from "react-hook-form";
import { FaChevronLeft } from "react-icons/fa6";
import { Button } from "primereact/button";

import { MessageResponseDto } from "@api/api";
import { useFormApiMapper } from "@core/services/form-api-mapper.hook";
import { useQyCreateEmployeeSalary } from "@core/query/employee-salary.query";
import {
  EmployeeSalaryFormRule,
  EmployeeSalaryFormSchema,
} from "@core/form/form.rule";
import { employeeSalaryFormDefault } from "@core/form/form.default";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import HeaderContent from "@shared/ui/header-content/header-content";
import FormEmployeeSalary from "../form-employee-salary/form-employee-salary";
import { useFormDropdownPersonContext } from "@domain/person/form-dropdown-person/form-dropdown-person.context";

export function EmployeeSalary() {
  // LOCAL DECLARATION
  const { selectedPersonData } = useFormDropdownPersonContext();
  const navigate = useNavigate();
  const { mapCreateEmployeeSalary } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();

  // QUERIES
  const handleApiSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;

    showSuccess(`New EmployeeSalary is created`);
    navigate(`../${data.code}`);
  };
  const { mutate: addEmployeeSalary, isLoading: isCreating } =
    useQyCreateEmployeeSalary(handleApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<EmployeeSalaryFormSchema>({
    defaultValues: employeeSalaryFormDefault,
    resolver: zodResolver(EmployeeSalaryFormRule),
  });
  const { handleSubmit } = formMethod;

  const handleValidate = (form: EmployeeSalaryFormSchema) => {
    const updatedForm = {
      ...form,
      bucketCode: selectedPersonData?.bucket.code,
      userCode: selectedPersonData?.code,
    } as EmployeeSalaryFormSchema;
    const formData = mapCreateEmployeeSalary(updatedForm);
    addEmployeeSalary(formData);
  };
  const handleValidateError = (err: FieldErrors<EmployeeSalaryFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  return (
    <div id="EmployeeSalary" className="employee-salary">
      <HeaderContent
        title="New Employee Salary"
        backIcon={<FaChevronLeft />}
        onBack={() => navigate("../")}
      >
        <Button
          className="w-full block"
          label="Save"
          disabled={isCreating}
          onClick={handleSubmit(handleValidate, handleValidateError)}
        ></Button>
      </HeaderContent>

      <div className="p-7">
        <FormProvider {...formMethod}>
          <section className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4 bg-white">
            <FormEmployeeSalary />
          </section>
        </FormProvider>
      </div>
    </div>
  );
}

export default EmployeeSalary;
